import { isTelloportBackendUrl, registerApiRequestFailureListener, registerApiRequestSuccessListener } from '@frontend/api-utils';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AndroidWebViewNetworkStatus } from '../../android-webview/android-webview-app';
import { MANUAL_REFRESH_MIN_MS, getVerifyBackoffMs, nextVerifyBackoffStep } from './network-check-interval';
import { resolveNetworkStatus } from './resolve-network-status';

interface NetworkStatus {
    deviceConnected: boolean;
    networkConnected: boolean;
    backendConnected: boolean;
    connected: boolean;
    checking: boolean;
    refresh: () => void;
}

function createOptimisticStatus(): AndroidWebViewNetworkStatus {
    const online = navigator.onLine;

    return {
        deviceConnected: online,
        networkConnected: online,
        backendConnected: online
    };
}

/**
 * Reacts to failed API requests and browser online/offline events instead of polling.
 * When a network-related request fails, connectivity is verified once (native bridge or fallback probes).
 */
export const useNetworkConnected = (): NetworkStatus => {
    const [status, setStatus] = useState<AndroidWebViewNetworkStatus>(createOptimisticStatus);
    const [checking, setChecking] = useState(false);

    const verifyBackoffStepRef = useRef(0);
    const verifyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const checkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const verifyInFlightRef = useRef(false);
    const verifyQueuedRef = useRef(false);
    const manualRefreshPendingRef = useRef(0);
    const manualRefreshStartedAtRef = useRef<number | null>(null);

    const applyStatus = useCallback((nextStatus: AndroidWebViewNetworkStatus) => {
        setStatus(nextStatus);
    }, []);

    const clearScheduledVerify = useCallback(() => {
        if (verifyTimeoutRef.current !== null) {
            clearTimeout(verifyTimeoutRef.current);
            verifyTimeoutRef.current = null;
        }
    }, []);

    const clearCheckingTimeout = useCallback(() => {
        if (checkingTimeoutRef.current !== null) {
            clearTimeout(checkingTimeoutRef.current);
            checkingTimeoutRef.current = null;
        }
    }, []);

    const finishManualRefreshChecking = useCallback(() => {
        if (manualRefreshPendingRef.current <= 0) {
            return;
        }

        clearCheckingTimeout();

        const startedAt = manualRefreshStartedAtRef.current ?? Date.now();
        const remaining = Math.max(0, MANUAL_REFRESH_MIN_MS - (Date.now() - startedAt));

        const clearChecking = () => {
            manualRefreshPendingRef.current = 0;
            manualRefreshStartedAtRef.current = null;
            checkingTimeoutRef.current = null;
            setChecking(false);
        };

        if (remaining === 0) {
            clearChecking();
            return;
        }

        checkingTimeoutRef.current = setTimeout(clearChecking, remaining);
    }, [clearCheckingTimeout]);

    const runVerify = useCallback(async () => {
        if (verifyInFlightRef.current) {
            verifyQueuedRef.current = true;
            return;
        }

        verifyInFlightRef.current = true;
        verifyQueuedRef.current = false;

        try {
            const nextStatus = await resolveNetworkStatus();
            applyStatus(nextStatus);

            verifyBackoffStepRef.current = nextVerifyBackoffStep(verifyBackoffStepRef.current, nextStatus.backendConnected);
        } finally {
            verifyInFlightRef.current = false;

            if (verifyQueuedRef.current) {
                verifyQueuedRef.current = false;
                void runVerify();
            } else if (manualRefreshPendingRef.current > 0) {
                finishManualRefreshChecking();
            }
        }
    }, [applyStatus, finishManualRefreshChecking]);

    const scheduleVerify = useCallback(
        (resetBackoff = false) => {
            if (resetBackoff) {
                verifyBackoffStepRef.current = 0;
            }

            clearScheduledVerify();
            verifyTimeoutRef.current = setTimeout(() => {
                void runVerify();
            }, getVerifyBackoffMs(verifyBackoffStepRef.current));
        },
        [clearScheduledVerify, runVerify]
    );

    const handleApiSuccess = useCallback(
        (url: string) => {
            if (!navigator.onLine) {
                return;
            }

            verifyBackoffStepRef.current = 0;
            clearScheduledVerify();

            setStatus((current) => ({
                deviceConnected: true,
                networkConnected: true,
                backendConnected: isTelloportBackendUrl(url) ? true : current.backendConnected
            }));
        },
        [clearScheduledVerify]
    );

    useEffect(() => {
        const handleOffline = () => {
            clearScheduledVerify();
            applyStatus({
                deviceConnected: false,
                networkConnected: false,
                backendConnected: false
            });
        };

        const handleOnline = () => {
            scheduleVerify(true);
        };

        const unregisterFailure = registerApiRequestFailureListener(() => {
            if (verifyInFlightRef.current) {
                return;
            }

            scheduleVerify();
        });
        const unregisterSuccess = registerApiRequestSuccessListener(({ url }) => {
            handleApiSuccess(url);
        });

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);

        return () => {
            unregisterFailure();
            unregisterSuccess();
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
            clearScheduledVerify();
            clearCheckingTimeout();
        };
    }, [applyStatus, clearCheckingTimeout, clearScheduledVerify, handleApiSuccess, scheduleVerify]);

    const refresh = useCallback(() => {
        clearCheckingTimeout();
        manualRefreshPendingRef.current += 1;
        manualRefreshStartedAtRef.current = Date.now();
        setChecking(true);
        verifyBackoffStepRef.current = 0;
        clearScheduledVerify();
        void runVerify();
    }, [clearCheckingTimeout, clearScheduledVerify, runVerify]);

    return {
        deviceConnected: status.deviceConnected,
        networkConnected: status.networkConnected,
        backendConnected: status.backendConnected,
        // Banner visibility is driven by IoT backend reachability; public DNS is not required.
        connected: status.deviceConnected && status.backendConnected,
        checking,
        refresh
    };
};
